import { DispatchAction } from "./State.js";
import { toggleInitiativeReducer } from "./UiReducer";

/**
 * @type {{string: string}}
 */
export const GameActions = {
  start: "start_game",
  end: "end_game",
  rematch: "rematch_game",
  damage: "damage_base",
  heal: "heal_base",
  claimInitiative: "claim_initiative",
  useEpicAction: "use_epic_action",
  setWinner: "set_winner",
};

const LogTypes = {
  damage: "Damage",
  heal: "Heal",
  initiative: "Initiative",
  epicAction: "EpicAction",
  gameStart: "GameStart",
  gameEnd: "GameEnd",
};

/**
 * @name addLogReducer
 * @param {SwucState} state
 * @param {string} message
 * @param {string} type
 * @param {"top" | "bottom"} player
 * @param {number} amount
 * @return {SwucState}
 */
function addLogReducer(state, { message, type, player, amount = 0 }) {
  const newState = { ...state };
  newState.currentGame.log.push({
    player,
    message,
    type,
    amount,
    timestamp: Date.now(),
    timeInGame: Date.now() - newState.currentGame.startTime,
  });
  return newState;
}

/**
 * @name setWinnerReducer
 * @param {SwucState} state
 * @param {"top" | "bottom"} player
 * @param {string} reason
 * @return {SwucState}
 */
function setWinnerReducer(state, { player, reason }) {
  let newState = { ...state };
  newState.currentGame.ended = true;
  newState.currentGame.winner = player;
  newState.currentGame.endTime = Date.now();
  newState.games.push(newState.currentGame);
  newState.ui.showPickWinnerModal = { player: "none", show: false };
  newState = addLogReducer(newState, {
    message: reason,
    type: LogTypes.gameEnd,
    player,
  });
  DispatchAction(GameActions.end);
  return newState;
}

/**
 * @name startGameReducer
 * @param {SwucState} state
 * @return {SwucState}
 */
function startGameReducer(state) {
  const newState = { ...state };
  newState.currentGame = {
    rounds: 1,
    topBase: newState.top,
    bottomBase: newState.bottom,
    log: [
      {
        message: "Game started",
        timestamp: Date.now(),
        type: LogTypes.gameStart,
        timeInGame: 0,
        player: "none",
      },
    ],
    startTime: Date.now(),
    ended: false,
  };

  return toggleInitiativeReducer(newState, { player: "bottom", show: true });
}

function endGameReducer(state) {
  let newState = { ...state };
  newState = addLogReducer(newState, {
    message: `Gave over`,
    type: LogTypes.damage,
    player: "none",
  });
  newState.currentGame.ended = true;
  newState.currentGame.endTime = Date.now();
  newState.games.push(newState.currentGame);
  return newState;
}

/**
 * @name rematchGameReducer
 * @param {SwucState} state
 * @return {SwucState}
 */
function rematchGameReducer(state) {
  const newState = { ...state };
  newState.currentGame = {
    rounds: 1,
    topBase: newState.top,
    topLeader: newState.top.leader,
    bottomBase: newState.bottom,
    bottomLeader: newState.bottom.leader,
    log: [
      {
        message: "Game started",
        timestamp: Date.now(),
        type: LogTypes.gameStart,
        timeInGame: 0,
        player: "none",
      },
    ],
    startTime: Date.now(),
    ended: false,
  };
  const loser = state.currentGame.winner === "top" ? "bottom" : "top";

  newState.top.remainingHp = newState.top.base.hp;
  newState.bottom.remainingHp = newState.bottom.base.hp;
  newState.top.hasUsedEpicAction = false;
  newState.bottom.hasUsedEpicAction = false;
  newState[loser].hasInitiative = true;
  newState[state.currentGame.winner].hasInitiative = false;
  return newState;
}

/**
 * @name doDamage
 * @param {SwucState} state
 * @param {"top" | "bottom"} player
 * @param {number} amount
 * @return {SwucState}
 */
function doDamage(state, { player, amount }) {
  if (player !== "top" && player !== "bottom") {
    console.error("Invalid payload", player);
    return state;
  }
  if (state[player] === undefined) {
    console.error("Invalid state", state);
    return state;
  }
  let newState = { ...state };
  newState[player].remainingHp -= amount;

  if (newState[player].remainingHp <= 0) {
    const opponent = player === "top" ? "bottom" : "top";
    newState[player].remainingHp = 0;
    newState.currentGame.ended = true;
    newState.currentGame.winner = opponent;
    newState.currentGame.endTime = Date.now();
    newState.games.push(newState.currentGame);
    DispatchAction(GameActions.end);
  }
  newState = addLogReducer(newState, {
    message: `Deal ${amount} damage. (${newState[player].remainingHp + "/" + newState[player].base.hp} hp)`,
    type: LogTypes.damage,
    amount: amount,
    player,
  });

  return newState;
}

/**
 * @name healDamage
 * @param {SwucState} state
 * @param {"top" | "bottom"} player
 * @param {number} amount
 * @return {SwucState}
 */
function healDamage(state, { player, amount }) {
  if (player !== "top" && player !== "bottom") {
    console.error("Invalid payload", player);
    return state;
  }
  if (state[player] === undefined) {
    console.error("Invalid state", state);
    return state;
  }
  let newState = { ...state };
  newState[player].remainingHp += amount;
  if (newState[player].remainingHp > newState[player].base.hp) {
    newState[player].remainingHp = newState[player].base.hp;
  }
  newState = addLogReducer(newState, {
    message: `Heal ${amount} damage (${newState[player].remainingHp + "/" + newState[player].base.hp} hp)`,
    type: LogTypes.heal,
    amount: amount,
    player,
  });
  return newState;
}

/**
 * @name claimInitiative
 * @param {SwucState} state
 * @param player
 * @return {SwucState}
 */
function claimInitiative(state, player) {
  if (player !== "top" && player !== "bottom") {
    console.error("Invalid payload", player);
    return state;
  }
  if (state[player] === undefined) {
    console.error("Invalid state", state);
    return state;
  }
  const opponent = player === "top" ? "bottom" : "top";
  let newState = { ...state };
  newState[player].hasInitiative = true;
  newState[opponent].hasInitiative = false;

  let logMessage = `Claims initiative`;

  newState = addLogReducer(newState, {
    message: logMessage,
    type: LogTypes.initiative,
    player,
  });
  return newState;
}

/**
 * @name useEpicAction
 * @param {SwucState} state
 * @param player
 * @return {SwucState}
 */
function useEpicAction(state, player) {
  if (player !== "top" && player !== "bottom") {
    console.error("Invalid payload", player);
    return state;
  }
  if (state[player] === undefined) {
    console.error("Invalid state", state);
    return state;
  }

  if (state[player].base.epicAction === undefined) {
    console.error("No epic action available", player);
    return state;
  }
  let newState = { ...state };
  newState[player].hasUsedEpicAction = !newState[player].hasUsedEpicAction;
  newState = addLogReducer(newState, {
    message: `Uses epic action`,
    type: LogTypes.epicAction,
    player,
  });
  return newState;
}

/**
 * @type {{string: function(SwucState, string=): SwucState}}
 * */
export const GameReducers = {
  [GameActions.start]: startGameReducer,
  [GameActions.rematch]: rematchGameReducer,
  [GameActions.damage]: doDamage,
  [GameActions.heal]: healDamage,
  [GameActions.claimInitiative]: claimInitiative,
  [GameActions.useEpicAction]: useEpicAction,
  [GameActions.setWinner]: setWinnerReducer,
  [GameActions.end]: endGameReducer,
};
